import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, Button, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import { AccountBalance, CheckCircle, Warning } from '@mui/icons-material';

const AdminEscrow = () => {
  const { t } = useTranslation();
  const [escrows, setEscrows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEscrow, setSelectedEscrow] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);

  useEffect(() => { loadEscrows(); }, []);

  const loadEscrows = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/escrow');
      setEscrows(data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleRelease = async (id) => {
    try {
      await api.put(`/escrow/${id}/release`);
      loadEscrows();
    } catch (err) { console.error(err); }
  };

  const statusColors = {
    pending: 'warning', funded: 'info', partially_released: 'primary',
    released: 'success', disputed: 'error', refunded: 'default'
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <AccountBalance color="primary" />
        <Typography variant="h5" fontWeight={700}>Escrow Management</Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Escrow ID</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Milestones</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {escrows.map((escrow) => (
              <TableRow key={escrow._id} hover>
                <TableCell><strong>{escrow.escrowId}</strong></TableCell>
                <TableCell>{escrow.order?.orderNumber || 'N/A'}</TableCell>
                <TableCell><strong>${escrow.amount?.toLocaleString()}</strong></TableCell>
                <TableCell>
                  <Chip label={escrow.status} size="small" color={statusColors[escrow.status] || 'default'} />
                </TableCell>
                <TableCell>{escrow.milestones?.length || 0}</TableCell>
                <TableCell>{new Date(escrow.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => { setSelectedEscrow(escrow); setDetailDialog(true); }}>
                    View
                  </Button>
                  {escrow.status === 'funded' && (
                    <Button size="small" color="success" startIcon={<CheckCircle />}
                      onClick={() => handleRelease(escrow._id)}>
                      Release
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {escrows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>No escrow records</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Escrow: {selectedEscrow?.escrowId}</DialogTitle>
        <DialogContent>
          {selectedEscrow && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2"><strong>Amount:</strong> ${selectedEscrow.amount?.toLocaleString()}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {selectedEscrow.status}</Typography>
              <Typography variant="body2"><strong>Order:</strong> {selectedEscrow.order?.orderNumber}</Typography>

              {selectedEscrow.milestones?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>Milestones:</Typography>
                  {selectedEscrow.milestones.map((m, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #eee' }}>
                      <Typography variant="body2">{m.description}</Typography>
                      <Typography variant="body2">${m.amount?.toLocaleString()}</Typography>
                      <Chip label={m.status} size="small" />
                    </Box>
                  ))}
                </Box>
              )}

              {selectedEscrow.transactions?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>Transactions:</Typography>
                  {selectedEscrow.transactions.map((tx, i) => (
                    <Box key={i} sx={{ py: 0.5, borderBottom: '1px solid #eee' }}>
                      <Typography variant="body2">{tx.type} - ${tx.amount} - {new Date(tx.date).toLocaleString()}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminEscrow;
